import React, { useLayoutEffect, useRef, useState } from 'react';
import { useEditor } from '../../context/EditorContext';
import { formatGuidePosition, inchFraction } from '../../lib/guideSnap';

/**
 * While a ruler guide is dragged (or a new one is pulled out of a ruler) this overlay — rendered inside the page — shows
 * the guide's exact position next to the cursor: the position in the document unit (with the inch fraction when it sits
 * on a sixteenth), the distance from the opposite page edge, and what it snapped to (page centre, margin, another
 * guide). For a guide being pulled out of a ruler it also draws the dashed preview line across the page.
 *
 * Driven by state.guideDrag = { orientation, position, along, snap, preview } (SET_GUIDE_DRAG, cleared on mouse up).
 */
export const GuideDragOverlay = ({ zoom, pageWidth, pageHeight }) => {
  const { state } = useEditor();
  const drag = state.guideDrag;
  // the tooltip's real size (it widens with the snap chip) so it can be kept inside the page
  const tipRef = useRef(null);
  const [tipSize, setTipSize] = useState({ w: 150, h: 40 });
  // measured after every render (the snap chip changes the width mid-drag); the equality guard stops a re-render loop
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    const el = tipRef.current;
    if (!el) return;
    const w = Math.ceil(el.offsetWidth), h = Math.ceil(el.offsetHeight);
    if (w && h && (w !== tipSize.w || h !== tipSize.h)) setTipSize({ w, h });
  });
  if (!drag || !Number.isFinite(drag.position)) return null;
  const unit = state.canvasUnit || 'in';
  const horizontal = drag.orientation === 'horizontal';
  const size = horizontal ? pageHeight : pageWidth;
  const pos = drag.position;
  const main = formatGuidePosition(pos, unit);
  const frac = unit === 'in' ? inchFraction(pos) : null;
  const fromFar = formatGuidePosition(size - pos, unit);
  const snapLabel = drag.snap && drag.snap.kind !== 'tick' ? drag.snap.label : null;
  // tooltip near the cursor along the guide, on the near side of the line (above / left) when there is room, otherwise
  // on the far side — and always kept inside the page, which matters on narrow screens
  const TIP_W = tipSize.w, TIP_H = tipSize.h, GAP = 10;
  const pw = pageWidth * zoom, ph = pageHeight * zoom;
  const clamp = (v, max) => Math.min(Math.max(v, 4), Math.max(4, max - 4));
  const along = Number.isFinite(drag.along) ? drag.along * zoom : (horizontal ? pw : ph) * 0.5;
  const p = pos * zoom;
  let left, top;
  if (horizontal) {
    left = clamp(along + GAP, pw - TIP_W);
    top = clamp(p - TIP_H - GAP >= 4 ? p - TIP_H - GAP : p + GAP, ph - TIP_H);
  } else {
    top = clamp(along + GAP, ph - TIP_H);
    left = clamp(p - TIP_W - GAP >= 4 ? p - TIP_W - GAP : p + GAP, pw - TIP_W);
  }
  return (
    <>
      {drag.preview && (
        <div
          className="absolute pointer-events-none"
          style={{ zIndex: 999, ...(horizontal ? { left: 0, right: 0, top: p - 0.5, height: 0, borderTop: '1px dashed hsl(var(--accent))' } : { top: 0, bottom: 0, left: p - 0.5, width: 0, borderLeft: '1px dashed hsl(var(--accent))' }) }}
          aria-hidden="true"
          data-testid="guide-drag-preview"
        />
      )}
      <div
        className="absolute pointer-events-none rounded-md border border-border/60 bg-popover/95 backdrop-blur-md shadow-lg px-2 py-1 text-foreground whitespace-nowrap select-none"
        ref={tipRef}
        style={{ zIndex: 1002, left, top }}
        role="status"
        aria-live="polite"
        data-testid="guide-drag-tip"
        data-snap={drag.snap ? drag.snap.kind : 'free'}
      >
        <div className="flex items-baseline gap-1.5">
          <span className="text-[13px] font-semibold tabular-nums" data-testid="guide-drag-tip-position">{main}</span>
          {frac && <span className="text-[11px] text-muted-foreground tabular-nums" data-testid="guide-drag-tip-fraction">{frac}"</span>}
        </div>
        <div className="text-[11px] text-muted-foreground tabular-nums">
          {horizontal ? 'from bottom' : 'from right'} {fromFar}
          {snapLabel && <span className="ml-1.5 text-accent-foreground bg-accent/80 rounded px-1" data-testid="guide-drag-tip-snap">{snapLabel}</span>}
        </div>
      </div>
    </>
  );
};
